import { useEffect, useState } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';

import { Paginated } from 'models/Paginated';

/**
 * Generic hook that wraps any paginated index API function into a shape
 * compatible with <Select> and <MultiSelectChips> from InfiniteScrollDropdown.
 *
 * Returns `{ options, onDebouncedSearch }` — spread directly into Select props.
 *
 * Usage:
 *   const episodeOptions = useInfiniteOptions({
 *     queryKey: ['adminClassifications', { type: 'episode' }],
 *     fetchFn: (params, signal) => adminIndexClassifications(params, signal),
 *     params: { type: 'episode', sortBy: 'name asc' },
 *   });
 *
 *   <MultiSelectChips {...episodeOptions} ... />
 */

type Config<T, P extends Record<string, any> = Record<string, any>> = {
  queryKey: readonly unknown[];
  fetchFn: (params: P & { page?: number; pageSize?: number; search?: string }, signal?: any) => Promise<Paginated<T>>;
  params?: P;
  pageSize?: number;
};

export default function useInfiniteOptions<T, P extends Record<string, any> = Record<string, any>>(
  config: Config<T, P>
) {
  const { queryKey, fetchFn, params = {} as P, pageSize = 25 } = config;
  const [search, setSearch] = useState<string>();

  const options = useInfiniteQuery({
    queryKey: [...queryKey, { search }],
    queryFn: ({ signal, pageParam }) => fetchFn({ ...params, search, page: pageParam, pageSize } as any, signal),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPage.meta.totalPages > lastPageParam ? lastPageParam + 1 : undefined,
    enabled: false,
  });

  useEffect(() => {
    if (search !== undefined) options.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return { options, onDebouncedSearch: setSearch };
}
